import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { IResource } from './IResource';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { WithLinks } from '../caremap/WithLinks';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { Logger } from '../support/Logger';

export const ONE_HOUR_MS = 1000 * 60 * 60;

export class ResourcesState extends State<Record<string, never>> {

    static inject = () => [
        APIClient,
        HistoryViewModel,
        MixPanelEventEmitter,
        BufferedEventBus,
        Logger
    ];
    constructor(
        private apiClient: APIClient,
        private history: HistoryViewModel,
        private mixpanel: MixPanelEventEmitter,
        private events: BufferedEventBus,
        private logger: Logger
    ) {
        super({});

        this.events.on('web-sockets.message', async unknownMessage => {
            const message = unknownMessage as IResourceChangedSocketMessage;
            const resource = this.resources.find(r => r.links.rating?.href === message.resourceToRefresh);
            if (resource) {
                try {
                    resource.rating = await this.apiClient.get(resource.links.rating);
                    this.setState({ });
                } catch (err: any) {
                    this.logger.error(`ResourcesState web-sockets.message handler failed updating rating for resourceId ${resource.resourceId}. Inner error: ${err.toString()}`);
                };
            }
        });
    }

    private loadedTimestamp = 0;

    private resources: WithLinks<IResource>[] = [];

    // consider stale if not loaded in the past hour
    // (usually resource data only changes overnight)
    isStale = () => Date.now() - this.loadedTimestamp > ONE_HOUR_MS;

    isLoaded = () => Boolean(this.loadedTimestamp);

    getCategories = () => {
        return Array.from(this.resources.reduce((a, c) => a.add(c.category), new Set<string>())).sort((c1, c2) => c1.localeCompare(c2));
    };

    getResources = (category?: string) => this.resources.filter(resource => {
        return !category || resource.category.toLowerCase() === category.toLowerCase();
    });

    getResourceById = (resourceId: string) => this.resources.find(r => r.resourceId === resourceId);

    getResourceBySlug = (slug: string) => this.resources.find(r => r.slug === slug);

    rateResource = async ({ resourceId, rating }: { resourceId: string, rating: number | undefined }) => {
        const resource = this.getResourceById(resourceId)!;

        // optmistically update my rating, so stars reflect the change
        const oldRating = resource.rating.myRating;
        resource.rating.myRating = rating;
        this.setState({});

        try {
            if (rating) {
                resource.rating = await this.apiClient.put(resource.links.rating, { rating });
            } else {
                resource.rating = await this.apiClient.delete(resource.links.rating);
            }
        } catch (e: any) {
            // revert optimistic update above
            resource.rating.myRating = oldRating;
            throw e;
        } finally {
            this.setState({});
        }
    };

    load = async () => {
        const entry = await this.apiClient.entry();
        const resources = await this.apiClient.get(entry.links.resources) as WithLinks<IResource>[];
        this.resources = [...resources.sort((r1, r2) => r1.name.localeCompare(r2.name))];

        this.loadedTimestamp = Date.now();
        this.setState({});
    };

    filterTo = ({ category, trackingFrom }: { category: string | undefined, trackingFrom: 'Filters' | 'List' | 'Details' }) => {
        this.mixpanel.track('Resource Category Selected', () => ({
            Category: category || 'All Resources',
            From: trackingFrom
        }));

        const url = (category ? `/resources/?category=${category.toLowerCase()}` : '/resources');
        if (url !== this.history.getCurrentLocation().pathname) {
            this.history.push(url);
        }
    };
};
